import { useCallback, useEffect, useState } from "react";
import useAdminLoad from "../../../hooks/useAdminLoad";
import * as crmApi from "../api/crmApi";
import useNow from "./useNow";

const REFRESH_MS = 5 * 60 * 1000;

/**
 * Today's sessions (`GET /api/crm/dashboard/today`) and the three actions on a card.
 *
 * The strip refreshes every 5 minutes while the tab is visible (a client may confirm from
 * the reminder email meanwhile). A background refresh that fails keeps the last answer
 * on screen: the initial read's failure is the one the zone reports.
 *
 * `complete` / `noShow` record attendance and patch the card from the server's answer;
 * both RESOLVE with that answer so the caller can open the session-note prompt.
 */
export default function useTodaySessions() {
  const [data, setData] = useState(null);
  const now = useNow(60000);
  const load = useCallback(async () => { setData(await crmApi.getToday()); }, []);
  const { loading, loadError, reload } = useAdminLoad(load);

  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        load().catch(() => {});
      }
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const patchAttendee = useCallback((bookingId, patch) => setData((d) => (d ? {
    ...d,
    cards: d.cards.map((c) => ({
      ...c,
      attendees: c.attendees.map((a) => (a.booking_id === bookingId ? { ...a, ...patch } : a)),
    })),
  } : d)), []);

  const record = useCallback(async (bookingId, outcome) => {
    const res = await crmApi.recordAttendance(bookingId, outcome);
    patchAttendee(bookingId, {
      attendance: outcome,
      ...(res?.pack ? { pack: { balance_id: res.pack.balance_id, session_number: res.pack.sessions_used, sessions_total: res.pack.sessions_total } } : {}),
    });
    return res;
  }, [patchAttendee]);

  const complete = useCallback((bookingId) => record(bookingId, "completed"), [record]);
  const noShow = useCallback((bookingId) => record(bookingId, "no_show"), [record]);
  const resend = useCallback((bookingId) => crmApi.resendReminder(bookingId), []);

  return { data, loading, loadError, reload, complete, noShow, resend, now };
}
